import React, { useMemo, useState } from "react";
import { Input, Tag } from "antd";
import {
    SearchOutlined,
    SafetyCertificateOutlined,
    CheckCircleOutlined,
    DownOutlined,
    UpOutlined,
    SwapOutlined,
} from "@ant-design/icons";
import { getFormattedAmount } from "Shared/Utils";
import { Tokens } from "Shared/Tokens";
import LedgerEventDetailDrawer from "Components/Drawers/LedgerEventDetailDrawer";

const formatQty = (val) => {
    const num = Number(val || 0);
    if (!Number.isFinite(num)) return "0";
    if (Math.abs(num) >= 1000) {
        return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
    }
    if (Math.abs(num) >= 1) {
        return num.toLocaleString("en-US", { maximumFractionDigits: 4 });
    }
    return num.toLocaleString("en-US", { maximumFractionDigits: 6 });
};

const formatDateGroupKey = (ms, timezone = "Asia/Kolkata") => {
    const t = Number(ms || 0);
    if (!t || !Number.isFinite(t)) return "Recorded Activity";
    try {
        return new Date(t).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            ...(timezone && timezone !== "local" ? { timeZone: timezone } : {}),
        });
    } catch (e) {
        return new Date(t).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    }
};

const formatTimeShort = (ms, timezone = "Asia/Kolkata") => {
    const t = Number(ms || 0);
    if (!t || !Number.isFinite(t)) return "";
    try {
        return new Date(t).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            ...(timezone && timezone !== "local" ? { timeZone: timezone } : {}),
        });
    } catch (e) {
        return new Date(t).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });
    }
};

const formatRelativeSyncTime = (lastSyncedAt) => {
    if (!lastSyncedAt) return "Verified with exchange";
    let ms = 0;
    if (typeof lastSyncedAt === "number") {
        ms = lastSyncedAt;
    } else if (typeof lastSyncedAt === "string") {
        ms = new Date(lastSyncedAt).getTime();
    } else if (lastSyncedAt._seconds) {
        ms = lastSyncedAt._seconds * 1000;
    } else if (lastSyncedAt.seconds) {
        ms = lastSyncedAt.seconds * 1000;
    }
    if (!ms || !Number.isFinite(ms)) return "Verified with exchange";
    const diffMins = Math.max(0, Math.floor((Date.now() - ms) / 60000));
    if (diffMins < 1) return "Verified just now";
    if (diffMins < 60) return `Verified ${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Verified ${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `Verified ${diffDays}d ago`;
};

/**
 * Converts manual portfolio assets & their transaction history into the same
 * Before -> After audited ledger event format as the Binance trade ledger.
 */
export const normalizeManualAssetsToLedgerEvents = (
    manualAssets = [],
    portfolios = [],
    cutoffDate = null
) => {
    if (!Array.isArray(manualAssets) || manualAssets.length === 0) return [];

    const portfolioMap = new Map();
    (portfolios || []).forEach((p) => {
        if (p && p.uid) portfolioMap.set(p.uid, p.name || "Manual");
    });

    const cutoffMs =
        cutoffDate &&
        cutoffDate !== "all" &&
        /^\d{4}-\d{2}-\d{2}$/.test(String(cutoffDate).trim())
            ? new Date(`${String(cutoffDate).trim()}T00:00:00.000Z`).getTime()
            : 0;

    const events = [];

    manualAssets.forEach((asset) => {
        if (!asset) return;
        const coin = String(
            asset.symbol || asset.id || asset.name || "COIN"
        ).toUpperCase();
        const portfolioName =
            portfolioMap.get(asset.portfolioId) ||
            asset.portfolioName ||
            "Manual Portfolio";
        const txList = Array.isArray(asset.transactions)
            ? asset.transactions
            : [];

        if (txList.length === 0) {
            const latestQty = Number(
                asset.portfolio?.latest?.numberOfCoins || 0
            );
            const latestInv = Number(asset.portfolio?.latest?.investment || 0);
            const latestPrice = Number(
                asset.portfolio?.latest?.marketPrice || 0
            );
            if (latestQty > 0 && latestInv > 0) {
                const rawTime = asset.createdAt
                    ? new Date(asset.createdAt).getTime()
                    : Date.now();
                const time = Number.isFinite(rawTime) ? rawTime : Date.now();
                if (cutoffMs > 0 && time < cutoffMs) return;
                events.push({
                    id: String(asset.uid || `${coin}-init`),
                    coin,
                    time,
                    type: "buy",
                    source: "manual",
                    origin: "manual",
                    portfolioName,
                    qty: latestQty,
                    quoteQty: latestInv,
                    unitPrice:
                        latestPrice > 0 ? latestPrice : latestInv / latestQty,
                    qtyDelta: latestQty,
                    costBasisDelta: latestInv,
                    costReleased: 0,
                    effectiveProceeds: 0,
                    beforeQty: 0,
                    afterQty: latestQty,
                    beforeCostBasis: 0,
                    afterCostBasis: latestInv,
                    beforeAvgPrice: 0,
                    afterAvgPrice:
                        latestPrice > 0 ? latestPrice : latestInv / latestQty,
                    realizedPlDelta: 0,
                    runningCoinRealizedPl: 0,
                    inActiveFifoWindow: true,
                    activeLotQty: latestQty,
                    valuationMethod: "manual_entry",
                });
            }
            return;
        }

        let runningRealized = 0;
        txList.forEach((tx, idx) => {
            if (!tx) return;
            const rawTime = tx.createdAt
                ? new Date(tx.createdAt).getTime()
                : Date.now() - (txList.length - idx) * 60000;
            const time = Number.isFinite(rawTime) ? rawTime : Date.now();
            if (cutoffMs > 0 && time < cutoffMs) return;

            const snap = tx.portfolioSnapshot || {};
            const beforeQty = Number(snap.previous?.numberOfCoins || 0);
            const afterQty = Number(snap.latest?.numberOfCoins || 0);
            const beforeCostBasis = Number(snap.previous?.investment || 0);
            const afterCostBasis = Number(snap.latest?.investment || 0);
            const beforeAvgPrice = Number(snap.previous?.marketPrice || 0);
            const afterAvgPrice = Number(snap.latest?.marketPrice || 0);
            const isSell = String(tx.type || "").toLowerCase() === "sell";
            const qty = Math.abs(afterQty - beforeQty);
            const unitPrice = Number(
                snap.transactionMarketPrice ||
                    afterAvgPrice ||
                    beforeAvgPrice ||
                    0
            );
            const quoteQty = qty * unitPrice;
            const costReleased = isSell
                ? Math.max(0, beforeCostBasis - afterCostBasis)
                : 0;
            const effectiveProceeds = isSell ? quoteQty : 0;
            const realizedPlDelta = isSell
                ? effectiveProceeds - costReleased
                : 0;
            runningRealized += realizedPlDelta;

            events.push({
                id: String(tx.uid || `${coin}-tx-${idx}`),
                coin,
                time,
                type: isSell ? "sell" : "buy",
                source: "manual",
                origin: "manual",
                portfolioName,
                qty,
                quoteQty,
                unitPrice,
                qtyDelta: isSell ? -qty : qty,
                costBasisDelta: afterCostBasis - beforeCostBasis,
                costReleased,
                effectiveProceeds,
                beforeQty,
                afterQty,
                beforeCostBasis,
                afterCostBasis,
                beforeAvgPrice,
                afterAvgPrice,
                realizedPlDelta,
                runningCoinRealizedPl: runningRealized,
                inActiveFifoWindow: !isSell && afterQty > 0,
                activeLotQty: !isSell ? qty : 0,
                valuationMethod: "manual_entry",
            });
        });
    });

    return events;
};

const SOURCE_FILTERS = [
    { key: "all", label: "All Sources" },
    { key: "binance", label: "Binance" },
    { key: "manual", label: "Manual" },
];

const TYPE_FILTERS = [
    { key: "all", label: "All Events" },
    { key: "buy", label: "Buys" },
    { key: "sell", label: "Sells" },
    { key: "auto-invest", label: "Auto-Invest" },
    { key: "convert", label: "Convert" },
    { key: "transfer", label: "Transfers" },
];

const getEventBadgeConfig = (ev) => {
    if (ev.origin === "manual" || ev.source === "manual") {
        const isSell = ev.type === "sell";
        return {
            label: isSell ? "MANUAL SELL" : "MANUAL BUY",
            bg: isSell ? Tokens.accentSubtle : Tokens.positiveBg,
            color: isSell ? Tokens.accent : Tokens.positive,
            border: isSell ? Tokens.accentBorder : Tokens.positiveBorder,
        };
    }
    if (ev.source === "auto-invest") {
        return {
            label: "AUTO-INVEST",
            bg: Tokens.positiveBg,
            color: Tokens.positive,
            border: Tokens.positiveBorder,
        };
    }
    if (ev.source === "convert") {
        return {
            label: ev.type === "sell" ? "CONVERT OUT" : "CONVERT IN",
            bg: Tokens.accentSubtle,
            color: Tokens.accent,
            border: Tokens.accentBorder,
        };
    }
    if (ev.type === "sell") {
        return {
            label: "SPOT SELL",
            bg: Tokens.accentSubtle,
            color: Tokens.accent,
            border: Tokens.accentBorder,
        };
    }
    if (ev.type === "deposit") {
        return {
            label: "DEPOSIT",
            bg: Tokens.surface,
            color: Tokens.textSecondary,
            border: Tokens.hairline,
        };
    }
    if (ev.type === "withdrawal") {
        return {
            label: "WITHDRAWAL",
            bg: Tokens.warningBg,
            color: Tokens.warning,
            border: Tokens.warningBorder,
        };
    }
    return {
        label: "SPOT BUY",
        bg: Tokens.positiveBg,
        color: Tokens.positive,
        border: Tokens.positiveBorder,
    };
};

const MoneyLedgerView = ({
    ledgerData = null,
    reportCard = null,
    manualAssets = [],
    portfolios = [],
    cutoffDate = "2026-01-01",
    currencySymbol = "$",
    exchangeRate = 1,
    bookMode = null,
    onBookModeChange = null,
    timezone = "Asia/Kolkata",
}) => {
    const [internalBookMode, setInternalBookMode] = useState("ledger");
    const activeBookMode = bookMode || internalBookMode;
    const handleBookModeChange = (nextMode) => {
        setInternalBookMode(nextMode);
        if (typeof onBookModeChange === "function") {
            onBookModeChange(nextMode);
        }
    };

    const [sourceFilter, setSourceFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [showAuditProof, setShowAuditProof] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isMobileView] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth < 520 : false
    );

    const rate =
        Number.isFinite(Number(exchangeRate)) && Number(exchangeRate) > 0
            ? Number(exchangeRate)
            : 1;

    const money = (val, isManualCurrency = false) => {
        const mult = isManualCurrency ? 1 : rate;
        return `${currencySymbol}${getFormattedAmount(
            Math.abs(Number(val || 0) * mult)
        )}`;
    };

    const usdtFmt = (val) =>
        `${getFormattedAmount(Math.abs(Number(val || 0)))} USDT`;

    const signed = (val) => (Number(val || 0) >= 0 ? "+" : "-");

    const liveUsdtCash = useMemo(() => {
        const fromProof = Number(
            ledgerData?.reconciliationProof?.usdtBalance || 0
        );
        if (fromProof > 0) return fromProof;
        const fromReport = Number(reportCard?.usdtBalance || 0);
        if (fromReport > 0) return fromReport;
        try {
            const cached = JSON.parse(
                localStorage.getItem("binance_wallet_summary_cache") || "{}"
            );
            const rawUsdt = Number(cached?.usdtBalance || 0);
            if (rawUsdt > 0) {
                return rate > 0 ? rawUsdt / rate : rawUsdt;
            }
        } catch (e) {
            // ignore storage errors
        }
        return 0;
    }, [ledgerData, reportCard, rate]);

    const manualEvents = useMemo(
        () =>
            normalizeManualAssetsToLedgerEvents(
                manualAssets,
                portfolios,
                cutoffDate
            ),
        [manualAssets, portfolios, cutoffDate]
    );

    const combinedEvents = useMemo(() => {
        const binanceEvents = (ledgerData?.events || []).map((ev) => ({
            ...ev,
            origin: "binance",
        }));
        const allAsc = [...binanceEvents, ...manualEvents].sort(
            (a, b) => Number(a.time || 0) - Number(b.time || 0)
        );

        let curCashUsdt = Math.max(0, Number(liveUsdtCash || 0));
        for (let i = allAsc.length - 1; i >= 0; i -= 1) {
            const ev = allAsc[i];
            const investedAfter =
                ev.runningTotalInvested !== undefined
                    ? Number(ev.runningTotalInvested)
                    : Number(ev.afterCostBasis || 0);
            let usdtCashDelta = 0;
            if (ev.type === "buy") {
                usdtCashDelta = -Number(ev.quoteQty || ev.costBasisDelta || 0);
            } else if (ev.type === "sell") {
                usdtCashDelta = Number(
                    ev.effectiveProceeds || ev.quoteQty || 0
                );
            } else if (ev.type === "deposit") {
                usdtCashDelta = Number(ev.quoteQty || 0);
            } else if (ev.type === "withdrawal") {
                usdtCashDelta = -Number(ev.quoteQty || ev.costReleased || 0);
            }

            const afterCash =
                liveUsdtCash > 0
                    ? Math.max(0, curCashUsdt)
                    : Number(ev.runningCashUsdt || 0);
            const beforeCash =
                liveUsdtCash > 0 ? Math.max(0, afterCash - usdtCashDelta) : 0;

            ev.computedCashDeltaUsdt = usdtCashDelta;
            ev.computedAfterCashUsdt = afterCash;
            ev.computedBeforeCashUsdt = beforeCash;
            ev.computedInvestedUsdt = investedAfter;
            ev.computedAccountUsdt = investedAfter + afterCash;
            curCashUsdt = beforeCash;
        }

        if (sortOrder === "desc") {
            allAsc.reverse();
        }
        return allAsc;
    }, [ledgerData, manualEvents, sortOrder, liveUsdtCash]);

    const filteredEvents = useMemo(() => {
        return combinedEvents.filter((ev) => {
            if (sourceFilter === "binance" && ev.origin !== "binance") {
                return false;
            }
            if (sourceFilter === "manual" && ev.origin !== "manual") {
                return false;
            }
            if (typeFilter === "buy" && ev.type !== "buy") {
                return false;
            }
            if (typeFilter === "sell" && ev.type !== "sell") {
                return false;
            }
            if (typeFilter === "auto-invest" && ev.source !== "auto-invest") {
                return false;
            }
            if (typeFilter === "convert" && ev.source !== "convert") {
                return false;
            }
            if (
                typeFilter === "transfer" &&
                ev.type !== "deposit" &&
                ev.type !== "withdrawal"
            ) {
                return false;
            }
            if (searchQuery.trim()) {
                const q = searchQuery.trim().toLowerCase();
                const matchCoin = String(ev.coin || "")
                    .toLowerCase()
                    .includes(q);
                const matchSource = String(ev.source || "")
                    .toLowerCase()
                    .includes(q);
                const matchPortfolio = String(ev.portfolioName || "")
                    .toLowerCase()
                    .includes(q);
                if (!matchCoin && !matchSource && !matchPortfolio) return false;
            }
            return true;
        });
    }, [combinedEvents, sourceFilter, typeFilter, searchQuery]);

    const dateGroups = useMemo(() => {
        const groups = [];
        const groupMap = new Map();
        filteredEvents.forEach((ev) => {
            const label = formatDateGroupKey(ev.time, timezone);
            if (!groupMap.has(label)) {
                const group = { dateLabel: label, items: [] };
                groupMap.set(label, group);
                groups.push(group);
            }
            groupMap.get(label).items.push(ev);
        });
        return groups;
    }, [filteredEvents, timezone]);

    const proof = ledgerData?.reconciliationProof || {};
    const counts = proof.countsBySource || {};
    const activeCoinsCount =
        proof.activeCoinsCount ?? reportCard?.activeCoinsCount ?? 0;
    const reconciledCoinsCount = proof.reconciledCoinsCount ?? activeCoinsCount;
    const openInvested = Number(
        proof.reconciledOpenCostBasis ?? reportCard?.openInvested ?? 0
    );
    const currentOpenValue = Number(reportCard?.currentOpenValue ?? 0);
    const unrealizedPL = Number(reportCard?.lifetimeUnrealizedPL ?? 0);
    const realizedPL = Number(
        reportCard?.lifetimeRealizedPL ?? proof.lifetimeRealizedPL ?? 0
    );
    const totalAccountUsdt = openInvested + liveUsdtCash;
    const liveAccountUsdt =
        (currentOpenValue > 0 ? currentOpenValue : openInvested) + liveUsdtCash;

    return (
        <div data-testid="money-ledger-view">
            {/* Internal Mode Switcher: Account USDT Ledger vs Tradebook (shown when standalone) */}
            {!onBookModeChange && (
                <div
                    data-testid="ledger-book-mode-switcher"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "6px",
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusControl,
                        padding: "4px",
                        marginBottom: "12px",
                    }}
                >
                    {[
                        {
                            key: "ledger",
                            label: "Account USDT Ledger",
                            testId: "book-mode-ledger",
                        },
                        {
                            key: "tradebook",
                            label: "Tradebook (Coins)",
                            testId: "book-mode-tradebook",
                        },
                    ].map((tab) => {
                        const active = activeBookMode === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                data-testid={tab.testId}
                                onClick={() => handleBookModeChange(tab.key)}
                                style={{
                                    minHeight: "32px",
                                    borderRadius: Tokens.radiusControl,
                                    border: active
                                        ? `1px solid ${Tokens.accent}`
                                        : "1px solid transparent",
                                    backgroundColor: active
                                        ? Tokens.accentSubtle
                                        : "transparent",
                                    color: active
                                        ? Tokens.textPrimary
                                        : Tokens.textSecondary,
                                    fontSize: "12px",
                                    fontWeight: active ? 600 : 500,
                                    fontFamily: Tokens.fontMono,
                                    cursor: "pointer",
                                }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* 1. Data Trust & Reconciliation Proof Header Card */}
            <div
                data-testid="ledger-reconciliation-card"
                style={{
                    backgroundColor: Tokens.card,
                    border: `1px solid ${Tokens.hairline}`,
                    borderRadius: Tokens.radiusPanel,
                    padding: "16px",
                    marginBottom: "16px",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginBottom: "12px",
                    }}
                >
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: Tokens.positive,
                            backgroundColor: Tokens.positiveBg,
                            border: `1px solid ${Tokens.positiveBorder}`,
                            borderRadius: Tokens.radiusTag,
                            padding: "3px 8px",
                        }}
                    >
                        <SafetyCertificateOutlined />
                        <span>
                            {activeCoinsCount > 0
                                ? `${reconciledCoinsCount}/${activeCoinsCount} Active Coins Verified with Binance`
                                : "Ledger Verified with Binance API"}
                        </span>
                    </div>

                    <span
                        style={{
                            fontSize: "11px",
                            color: Tokens.textMuted,
                            fontFamily: Tokens.fontMono,
                        }}
                    >
                        {formatRelativeSyncTime(
                            ledgerData?.lastSyncedAt || reportCard?.lastSyncedAt
                        )}
                    </span>
                </div>

                {/* Entire Account USDT Summary Banner */}
                {activeBookMode === "ledger" && (
                    <div
                        data-testid="account-usdt-summary-banner"
                        style={{
                            backgroundColor: Tokens.surface,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            padding: "10px 12px",
                            marginBottom: "10px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: "10px",
                                    color: Tokens.textMuted,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                Entire Account Balance (USDT)
                            </div>
                            <div
                                style={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: Tokens.textPrimary,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {usdtFmt(totalAccountUsdt)}
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "4px 12px",
                                fontSize: "11px",
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                color: Tokens.textSecondary,
                            }}
                        >
                            <span style={{ whiteSpace: "nowrap" }}>
                                Crypto:{" "}
                                <strong style={{ color: Tokens.textPrimary }}>
                                    {usdtFmt(openInvested)}
                                </strong>
                            </span>
                            <span style={{ whiteSpace: "nowrap" }}>
                                Cash:{" "}
                                <strong style={{ color: Tokens.textPrimary }}>
                                    {usdtFmt(liveUsdtCash)}
                                </strong>
                            </span>
                            {currentOpenValue > 0 && (
                                <span style={{ whiteSpace: "nowrap" }}>
                                    Live:{" "}
                                    <strong style={{ color: Tokens.accent }}>
                                        {usdtFmt(liveAccountUsdt)}
                                    </strong>
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Accounting Identity Equation Strip */}
                <div
                    data-testid="ledger-accounting-identity"
                    style={{
                        backgroundColor: Tokens.surface,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "12px",
                        display: "grid",
                        gridTemplateColumns: isMobileView
                            ? "repeat(2, minmax(0, 1fr))"
                            : "repeat(4, minmax(0, 1fr))",
                        gap: "10px 12px",
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: "10px",
                                color: Tokens.textMuted,
                                marginBottom: "2px",
                            }}
                        >
                            Open Cost
                        </div>
                        <div
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {money(openInvested)}
                        </div>
                    </div>
                    <div>
                        <div
                            style={{
                                fontSize: "10px",
                                color: Tokens.textMuted,
                                marginBottom: "2px",
                            }}
                        >
                            + Unrealized
                        </div>
                        <div
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color:
                                    unrealizedPL >= 0
                                        ? Tokens.positive
                                        : Tokens.negative,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {signed(unrealizedPL)}
                            {money(unrealizedPL)}
                        </div>
                    </div>
                    <div>
                        <div
                            style={{
                                fontSize: "10px",
                                color: Tokens.textMuted,
                                marginBottom: "2px",
                            }}
                        >
                            = Live Value
                        </div>
                        <div
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {money(currentOpenValue)}
                        </div>
                    </div>
                    <div>
                        <div
                            style={{
                                fontSize: "10px",
                                color: Tokens.textMuted,
                                marginBottom: "2px",
                            }}
                        >
                            Realized P&amp;L
                        </div>
                        <div
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color:
                                    realizedPL >= 0
                                        ? Tokens.positive
                                        : Tokens.negative,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {signed(realizedPL)}
                            {money(realizedPL)}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        marginTop: "10px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "8px",
                    }}
                >
                    <span
                        style={{
                            fontSize: "11px",
                            color: Tokens.textSecondary,
                            fontFamily: Tokens.fontMono,
                        }}
                    >
                        {combinedEvents.length} audited events · Tap any row for
                        Before → After math
                    </span>
                    <button
                        type="button"
                        data-testid="ledger-proof-toggle"
                        onClick={() => setShowAuditProof((prev) => !prev)}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: Tokens.accent,
                            fontSize: "11px",
                            fontWeight: 500,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: 0,
                        }}
                    >
                        <span>
                            {showAuditProof
                                ? "Hide audit proof"
                                : "How numbers are verified"}
                        </span>
                        {showAuditProof ? (
                            <UpOutlined style={{ fontSize: "10px" }} />
                        ) : (
                            <DownOutlined style={{ fontSize: "10px" }} />
                        )}
                    </button>
                </div>

                {showAuditProof && (
                    <div
                        data-testid="ledger-proof-details"
                        style={{
                            marginTop: "12px",
                            paddingTop: "12px",
                            borderTop: `1px solid ${Tokens.hairline}`,
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            fontSize: "12px",
                            color: Tokens.textSecondary,
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(120px, 1fr))",
                                gap: "8px",
                            }}
                        >
                            {[
                                {
                                    label: "Spot Fills",
                                    val: counts.spot || 0,
                                },
                                {
                                    label: "Auto-Invests",
                                    val: counts.autoInvest || 0,
                                },
                                {
                                    label: "Convert Swaps",
                                    val: counts.convert || 0,
                                },
                                {
                                    label: "Deposits / Withdrawals",
                                    val:
                                        (counts.deposit || 0) +
                                        (counts.withdrawal || 0),
                                },
                                {
                                    label: "Manual Trades",
                                    val: manualEvents.length,
                                },
                            ].map((item) => (
                                <div
                                    key={item.label}
                                    style={{
                                        backgroundColor: Tokens.surface,
                                        borderRadius: Tokens.radiusControl,
                                        padding: "6px 10px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <span style={{ fontSize: "11px" }}>
                                        {item.label}
                                    </span>
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            color: Tokens.textPrimary,
                                            fontFamily: Tokens.fontMono,
                                        }}
                                    >
                                        {item.val}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div
                            style={{
                                fontSize: "11px",
                                color: Tokens.textMuted,
                                lineHeight: 1.5,
                            }}
                        >
                            <CheckCircleOutlined
                                style={{
                                    color: Tokens.positive,
                                    marginRight: "6px",
                                }}
                            />
                            Every buy increases open cost basis; every sell
                            releases cost basis at the running weighted-average
                            buy price and books realized P&amp;L. Live Spot +
                            Simple Earn + Funding quantities are reconciled
                            against active FIFO purchase lots.
                        </div>
                    </div>
                )}
            </div>

            {/* 2. Source & Event Type Filter Chips (Clean Wrapped Rows, Zero Horizontal Scrollbar) */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    marginBottom: "10px",
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: "6px",
                    }}
                >
                    {SOURCE_FILTERS.map((f) => {
                        const active = sourceFilter === f.key;
                        return (
                            <button
                                key={f.key}
                                type="button"
                                data-testid={`ledger-source-${f.key}`}
                                onClick={() => setSourceFilter(f.key)}
                                style={{
                                    minHeight: "30px",
                                    padding: "4px 8px",
                                    borderRadius: Tokens.radiusControl,
                                    border: `1px solid ${
                                        active ? Tokens.accent : Tokens.hairline
                                    }`,
                                    backgroundColor: active
                                        ? Tokens.accentSubtle
                                        : Tokens.card,
                                    color: active
                                        ? Tokens.textPrimary
                                        : Tokens.textSecondary,
                                    fontSize: "11px",
                                    fontWeight: active ? 600 : 400,
                                    fontFamily: Tokens.fontMono,
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                }}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                    }}
                >
                    {TYPE_FILTERS.map((f) => {
                        const active = typeFilter === f.key;
                        return (
                            <button
                                key={f.key}
                                type="button"
                                data-testid={`ledger-type-${f.key}`}
                                onClick={() => setTypeFilter(f.key)}
                                style={{
                                    minHeight: "28px",
                                    padding: "3px 9px",
                                    borderRadius: Tokens.radiusTag,
                                    border: `1px solid ${
                                        active ? Tokens.accent : Tokens.hairline
                                    }`,
                                    backgroundColor: active
                                        ? Tokens.accentSubtle
                                        : Tokens.card,
                                    color: active
                                        ? Tokens.textPrimary
                                        : Tokens.textSecondary,
                                    fontSize: "11px",
                                    fontWeight: active ? 600 : 400,
                                    fontFamily: Tokens.fontMono,
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                }}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 3. Search & Sort Bar */}
            <div
                style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "16px",
                }}
            >
                <Input
                    prefix={
                        <SearchOutlined style={{ color: Tokens.textMuted }} />
                    }
                    placeholder="Filter by coin (e.g. SOL, XRP, BTC)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="ledger-search-input"
                    style={{
                        backgroundColor: Tokens.card,
                        borderColor: Tokens.hairline,
                        borderRadius: Tokens.radiusControl,
                    }}
                />
                <button
                    type="button"
                    data-testid="ledger-sort-toggle"
                    onClick={() =>
                        setSortOrder((prev) =>
                            prev === "desc" ? "asc" : "desc"
                        )
                    }
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "0 12px",
                        borderRadius: Tokens.radiusControl,
                        border: `1px solid ${Tokens.hairline}`,
                        backgroundColor: Tokens.card,
                        color: Tokens.textSecondary,
                        fontSize: "12px",
                        fontFamily: Tokens.fontMono,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                    }}
                >
                    <SwapOutlined rotate={90} />
                    <span>{sortOrder === "desc" ? "Newest" : "Oldest"}</span>
                </button>
            </div>

            {/* 4. Chronological Date-Grouped Ledger Feed (2-Line Full-Width Mobile-First Rows) */}
            {dateGroups.map((group) => (
                <div key={group.dateLabel} style={{ marginBottom: "16px" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "4px 4px 6px",
                            fontSize: "11px",
                            fontWeight: 600,
                            color: Tokens.textMuted,
                            fontFamily: Tokens.fontMono,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                        }}
                    >
                        <span>{group.dateLabel}</span>
                        <span>
                            {group.items.length}{" "}
                            {group.items.length === 1 ? "event" : "events"}
                        </span>
                    </div>

                    <div
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            overflow: "hidden",
                        }}
                    >
                        {group.items.map((ev, idx) => {
                            const badge = getEventBadgeConfig(ev);
                            const isManual =
                                ev.origin === "manual" ||
                                ev.source === "manual";
                            const isPositiveQty =
                                ev.type === "buy" || ev.type === "deposit";
                            const isSell = ev.type === "sell";
                            const hasRealized =
                                isSell ||
                                Math.abs(Number(ev.realizedPlDelta || 0)) >
                                    1e-8;
                            const realizedPos =
                                Number(ev.realizedPlDelta || 0) >= 0;
                            const runningBalValue =
                                ev.runningTotalInvested !== undefined
                                    ? ev.runningTotalInvested
                                    : ev.afterCostBasis;
                            const accountBalUsdt =
                                ev.computedAccountUsdt !== undefined
                                    ? ev.computedAccountUsdt
                                    : runningBalValue;
                            const isLedgerMode = activeBookMode === "ledger";

                            return (
                                <div
                                    key={`${ev.coin}-${ev.id}-${ev.time}`}
                                    role="button"
                                    tabIndex={0}
                                    data-testid="ledger-timeline-row"
                                    onClick={() => setSelectedEvent(ev)}
                                    onKeyPress={(e) => {
                                        if (e.key === "Enter") {
                                            setSelectedEvent(ev);
                                        }
                                    }}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "6px",
                                        padding: "12px 14px",
                                        borderBottom:
                                            idx < group.items.length - 1
                                                ? `1px solid ${Tokens.hairline}`
                                                : "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    {/* Line 1: Coin + Event Badge + Time on left, Dollar Amount + Realized P&L on right */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: "8px",
                                            width: "100%",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                minWidth: 0,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: "14px",
                                                    color: Tokens.textPrimary,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {ev.coin}
                                            </span>
                                            <Tag
                                                style={{
                                                    margin: 0,
                                                    backgroundColor: badge.bg,
                                                    color: badge.color,
                                                    border: `1px solid ${badge.border}`,
                                                    borderRadius:
                                                        Tokens.radiusTag,
                                                    fontFamily: Tokens.fontMono,
                                                    fontSize: "10px",
                                                    padding: "0 5px",
                                                    lineHeight: "16px",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {badge.label}
                                            </Tag>
                                            {formatTimeShort(
                                                ev.time,
                                                timezone
                                            ) && (
                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        color: Tokens.textMuted,
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        whiteSpace: "nowrap",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {formatTimeShort(
                                                        ev.time,
                                                        timezone
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                fontFamily: Tokens.fontMono,
                                                fontVariantNumeric:
                                                    "tabular-nums",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    color: Tokens.textPrimary,
                                                }}
                                            >
                                                {(() => {
                                                    if (!(ev.quoteQty > 0)) {
                                                        return "Transfer";
                                                    }
                                                    if (isLedgerMode) {
                                                        const cashSign =
                                                            ev.type === "buy" ||
                                                            ev.type ===
                                                                "withdrawal"
                                                                ? "-"
                                                                : "+";
                                                        return `${cashSign}${usdtFmt(
                                                            ev.quoteQty
                                                        )}`;
                                                    }
                                                    return money(
                                                        ev.quoteQty,
                                                        isManual
                                                    );
                                                })()}
                                            </span>
                                            {hasRealized && (
                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 500,
                                                        color: realizedPos
                                                            ? Tokens.positive
                                                            : Tokens.negative,
                                                    }}
                                                >
                                                    (
                                                    {signed(ev.realizedPlDelta)}
                                                    {money(
                                                        ev.realizedPlDelta,
                                                        isManual
                                                    )}
                                                    )
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Line 2: Account USDT Breakdown (Ledger) OR Coin Execution & Holdings (Tradebook) */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: "8px",
                                            width: "100%",
                                            fontSize: "11px",
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: Tokens.textSecondary,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                minWidth: 0,
                                            }}
                                        >
                                            {isLedgerMode ? (
                                                <React.Fragment>
                                                    {ev.computedAfterCashUsdt >
                                                    0
                                                        ? `Cash: ${usdtFmt(
                                                              ev.computedAfterCashUsdt
                                                          )} · Crypto: ${usdtFmt(
                                                              ev.computedInvestedUsdt
                                                          )}`
                                                        : `Crypto: ${usdtFmt(
                                                              ev.computedInvestedUsdt
                                                          )} · ${
                                                              isPositiveQty
                                                                  ? "+"
                                                                  : "-"
                                                          }${formatQty(
                                                              ev.qty
                                                          )} ${ev.coin}`}
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    {isPositiveQty ? "+" : "-"}
                                                    {formatQty(ev.qty)}{" "}
                                                    {ev.coin}
                                                    {ev.unitPrice > 0
                                                        ? ` @ ${money(
                                                              ev.unitPrice,
                                                              isManual
                                                          )}`
                                                        : ""}
                                                    {` · ${formatQty(
                                                        ev.afterQty
                                                    )} ${ev.coin}`}
                                                </React.Fragment>
                                            )}
                                        </div>

                                        <div
                                            data-testid="ledger-row-running-balance"
                                            style={{
                                                color: Tokens.textPrimary,
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isLedgerMode
                                                ? `Bal: ${money(
                                                      accountBalUsdt,
                                                      isManual
                                                  )} USDT`
                                                : `Cost: ${money(
                                                      ev.afterCostBasis,
                                                      isManual
                                                  )} · Bal: ${money(
                                                      runningBalValue,
                                                      isManual
                                                  )}`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {filteredEvents.length === 0 && (
                <div
                    data-testid="ledger-empty-state"
                    style={{
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "36px 16px",
                        textAlign: "center",
                        color: Tokens.textSecondary,
                        fontSize: "13px",
                    }}
                >
                    No ledger events match the current filter.
                </div>
            )}

            <LedgerEventDetailDrawer
                event={selectedEvent}
                open={Boolean(selectedEvent)}
                onClose={() => setSelectedEvent(null)}
                currencySymbol={currencySymbol}
                exchangeRate={rate}
            />
        </div>
    );
};

export default MoneyLedgerView;
